import * as THREE from 'three';
import { ATMO, DAMAGE, FLIGHT } from '../config/tuning';
import type { Airframe } from '../config/airframes';
import type { Checksum, Checksummable } from '../core/Checksum';
import type { GroundSampler } from '../world/Terrain';
import type { Atmosphere } from './Atmosphere';
import {
  DamageState,
  applyAttrition,
  applyBlastDamage,
  destroyAirframe,
  spreadDamage,
} from '../combat/Damage';
import {
  angleOfAttackRad,
  clamp,
  controlAuthority,
  dragCoefficient,
  dynamicPressurePa,
  liftCoefficient,
  sideForceCoefficient,
  sideslipRad,
  stallDepth,
} from './Aerodynamics';
import {
  BODY_FORWARD,
  BODY_RIGHT,
  BODY_UP,
  FlightState,
  type ControlInput,
  type SpawnState,
} from './FlightState';

/**
 * Force assembly and integration (SPEC §3.2, §4.4).
 *
 * Semi-implicit Euler at 120 Hz for translation — stable at these accelerations and cheap.
 * Rotation integrates the body rates into the orientation quaternion with renormalisation each
 * tick. No general-purpose physics engine is involved, for the reasons in SPEC §4.4.
 *
 * Nothing in this file allocates. Every vector and quaternion it needs is a module-scope
 * scratch, reused across ticks; at 120 Hz, per-tick allocation is the most likely source of
 * frame-time spikes in the project.
 *
 * Units: metres, m/s, m/s², radians, rad/s, rad/s², kg, newtons, pascals, seconds.
 */

// --- Module-scope scratch. Never escapes a tick; never returned to a caller.
const windScratch = new THREE.Vector3();
const relativeVelocityScratch = new THREE.Vector3();
const bodyVelocityScratch = new THREE.Vector3();
const forceScratch = new THREE.Vector3();
const axisScratch = new THREE.Vector3();
const turbulenceScratch = new THREE.Vector3();
const rotationScratch = new THREE.Vector3();
const inverseOrientationScratch = new THREE.Quaternion();
const deltaOrientationScratch = new THREE.Quaternion();
const sweepScratch = new THREE.Vector3();
const blastRelative = new THREE.Vector3();
const inverseOrientation = new THREE.Quaternion();

export class FlightModel implements Checksummable {
  readonly state = new FlightState();
  readonly airframe: Airframe;

  private readonly atmosphere: Atmosphere;

  /**
   * The surface the aircraft can hit. Null means a flat plane at `groundHeightM`, which is what
   * the flight tests fly over — they are about aerodynamics, not about the theatre.
   */
  ground: GroundSampler | null = null;

  /** Ground height used when no sampler is attached, metres. */
  groundHeightM = 0;

  /**
   * Sea level, metres. The collision surface is the *higher* of terrain and sea, so the aircraft
   * hits the water rather than continuing down to the seabed 190 m below it (SPEC §4.4).
   */
  seaLevelM = 0;

  /** True once the aircraft has touched the surface. */
  crashed = false;

  private tickCount = 0;
  private fixedDtS = 0;

  constructor(airframe: Airframe, atmosphere: Atmosphere) {
    this.airframe = airframe;
    this.atmosphere = atmosphere;
  }

  /** Simulated seconds since spawn. Derived from the tick count, so it cannot drift. */
  get elapsedS(): number {
    return this.tickCount * this.fixedDtS;
  }

  /** Per-system battle damage (SPEC §3.8). Owned here; written by the combat systems. */
  readonly damage = new DamageState();

  /**
   * Take battle damage (SPEC §3.6.4). `fraction` is airframe integrity removed, 0–1.
   *
   * The consequences are already wired into the flight model: control authority falls away past
   * `DAMAGE.controlLossThreshold`, the engine quits past `engineLossThreshold`, and the airframe
   * comes apart past `structuralThreshold`. Nothing special happens here — which is the point.
   * A missile that hurts the aircraft hands it to the same damage model that an over-g pull does,
   * so a wounded drone flies like a wounded drone whatever wounded it.
   */
  applyDamage(fraction: number): void {
    if (fraction <= 0) return;
    // Spread across every system, not into `structure` alone.
    //
    // Structure carries 24% of the integrity weighting, so routing all damage there floors
    // integrity at 0.76 however many times the aircraft is hit — it becomes invulnerable, quietly,
    // and only a long flight taking twenty-one missile hits reveals it. Damage with no geometry
    // attached has no reason to favour one system, so it goes everywhere.
    spreadDamage(this.damage, fraction);
    this.syncDamageToState();
  }

  /**
   * Take a detonation at a world position (SPEC §3.8, M7).
   *
   * The blast is resolved in the airframe's own frame so that *where* it went off decides which
   * system it takes — a warhead off the left wing is a different aircraft afterwards from one off
   * the tail. `eventId` seeds the deterministic spread.
   */
  applyBlast(worldPositionM: THREE.Vector3, fraction: number, eventId: number): void {
    if (fraction <= 0) return;
    blastRelative.copy(worldPositionM).sub(this.state.position);
    inverseOrientation.copy(this.state.orientation).invert();
    blastRelative.applyQuaternion(inverseOrientation);
    applyBlastDamage(this.damage, blastRelative, fraction, eventId);
    this.syncDamageToState();
  }

  /** Push the derived damage numbers into the flight state the rest of the game reads. */
  private syncDamageToState(): void {
    const state = this.state;
    state.integrity = this.damage.integrity;
    if (this.damage.engineFailed) state.engineRunning = false;
    if (this.damage.structuralFailure) state.structuralFailure = true;
  }

  /** Place the aircraft at a starting condition and clear all accumulated damage. */
  spawn(spawn: SpawnState): void {
    this.state.spawn(this.airframe, spawn);
    this.damage.reset();
    this.tickCount = 0;
    this.crashed = false;
  }

  /** One fixed simulation tick. dtS is always the loop's fixed timestep. */
  tick(dtS: number, input: ControlInput): void {
    this.fixedDtS = dtS;
    const state = this.state;
    const airframe = this.airframe;

    state.storePreviousTransform();

    if (this.crashed) {
      this.tickCount += 1;
      return;
    }

    state.throttleCommand = clamp(input.throttle, 0, 1);

    // --- 1. Atmosphere -------------------------------------------------------------------
    const altitudeM = state.position.y;
    const density = this.atmosphere.densityAtM(altitudeM);
    state.airDensityKgM3 = density;
    this.atmosphere.sampleWind(altitudeM, this.elapsedS, windScratch);

    // --- 2. Relative airflow and the aerodynamic angles ----------------------------------
    relativeVelocityScratch.copy(state.velocity).sub(windScratch);
    const airspeedMs = relativeVelocityScratch.length();
    state.airspeedMs = airspeedMs;

    inverseOrientationScratch.copy(state.orientation).invert();
    bodyVelocityScratch.copy(relativeVelocityScratch).applyQuaternion(inverseOrientationScratch);

    const forwardMs = -bodyVelocityScratch.z;
    const upMs = bodyVelocityScratch.y;
    const rightMs = bodyVelocityScratch.x;

    const flying = airspeedMs >= FLIGHT.minAeroAirspeedMs;
    const alphaRad = flying ? angleOfAttackRad(forwardMs, upMs) : 0;
    const betaRad = flying ? sideslipRad(rightMs, airspeedMs) : 0;
    state.alphaRad = alphaRad;
    state.betaRad = betaRad;

    const qPa = dynamicPressurePa(density, airspeedMs);
    state.dynamicPressurePa = qPa;

    const depth = stallDepth(alphaRad, airframe);
    state.stallDepth = depth;
    state.stalled = depth > 0;

    // --- 3. Aerodynamic and propulsive forces --------------------------------------------
    const cl = flying ? liftCoefficient(alphaRad, airframe) : 0;
    const cd = flying ? dragCoefficient(cl, alphaRad, airframe) : 0;
    const cy = flying ? sideForceCoefficient(betaRad, airframe) : 0;

    const qS = qPa * airframe.wingAreaM2;
    const liftN = qS * cl;
    const dragN = qS * cd;
    const sideN = qS * cy;
    state.liftN = liftN;
    state.dragN = dragN;

    forceScratch.set(0, 0, 0);

    // Lift along the body-up axis (SPEC §3.2.2).
    axisScratch.copy(BODY_UP).applyQuaternion(state.orientation).multiplyScalar(liftN);
    forceScratch.add(axisScratch);

    // Side force along the body-right axis; Cy_β < 0, so it opposes the slip.
    axisScratch.copy(BODY_RIGHT).applyQuaternion(state.orientation).multiplyScalar(sideN);
    forceScratch.add(axisScratch);

    // Drag opposite the relative airflow.
    if (flying) {
      axisScratch.copy(relativeVelocityScratch).multiplyScalar(-dragN / airspeedMs);
      forceScratch.add(axisScratch);
    }

    // Thrust along the nose, scaled by density and lagging the command by thrustLagS.
    const lagFactor = 1 - Math.exp(-dtS / airframe.thrustLagS);
    state.throttleActual += (state.throttleCommand - state.throttleActual) * lagFactor;
    // A damaged engine still runs, just badly — thrust falls toward `engineThrustFloor` before
    // it quits outright. A drone limping home on 40% power is the outcome SPEC §3.6.4 wants most.
    const thrustN = state.engineRunning
      ? airframe.thrustMaxN *
        state.throttleActual *
        (density / ATMO.RHO_0) *
        this.damage.thrustFactor
      : 0;
    state.thrustN = thrustN;
    axisScratch.copy(BODY_FORWARD).applyQuaternion(state.orientation).multiplyScalar(thrustN);
    forceScratch.add(axisScratch);

    // Gravity, always.
    forceScratch.y -= state.massKg * ATMO.G;

    // --- 4. Rotation ---------------------------------------------------------------------
    this.integrateRotation(dtS, input, qPa, betaRad, depth, altitudeM);

    // --- 5. Translation: semi-implicit Euler ---------------------------------------------
    const inverseMass = 1 / state.massKg;
    state.velocity.addScaledVector(forceScratch, dtS * inverseMass);
    state.position.addScaledVector(state.velocity, dtS);

    // --- 6. Bookkeeping ------------------------------------------------------------------
    state.loadFactor = liftN / (state.massKg * ATMO.G);
    this.updateFuelAndDamage(dtS, airspeedMs);
    this.resolveGroundContact();

    this.tickCount += 1;
  }

  /**
   * Body-rate dynamics. The command terms are angular accelerations scaled by control
   * authority; the damping terms are rate-proportional; the coupling terms are what make the
   * aircraft read as a physical object rather than a set of three independent axes.
   */
  private integrateRotation(
    dtS: number,
    input: ControlInput,
    qPa: number,
    betaRad: number,
    depth: number,
    altitudeM: number,
  ): void {
    const state = this.state;
    const airframe = this.airframe;

    const p = state.angularVelocity.x;
    const q = state.angularVelocity.y;
    const r = state.angularVelocity.z;

    const authority = controlAuthority(qPa, airframe);

    // Authority is eaten by the stall and by control-system damage; a structural failure ends
    // it entirely.
    const damage = 1 - state.integrity;
    const damageLoss =
      damage > DAMAGE.controlLossThreshold
        ? FLIGHT.damageControlLoss *
          clamp((damage - DAMAGE.controlLossThreshold) / (1 - DAMAGE.controlLossThreshold), 0, 1)
        : 0;
    const stallLoss = FLIGHT.stallControlLoss * depth;
    const effectiveAuthority = state.structuralFailure
      ? 0
      : authority * (1 - stallLoss) * (1 - damageLoss);

    // Battle damage is per-surface (SPEC §3.8, M7). Losing one aileron costs roll authority *and*
    // leaves a standing roll the player has to hold off — the asymmetry has to be flown, not read.
    const rollAuthority = effectiveAuthority * this.damage.rollAuthorityFactor;
    const tailAuthority = effectiveAuthority * this.damage.tailAuthorityFactor;

    const rollCmd = clamp(input.rollCmd, -1, 1);
    const pitchCmd = clamp(input.pitchCmd, -1, 1);
    const yawCmd = clamp(input.yawCmd, -1, 1);

    // Roll damping falls away in the stall, which is what allows autorotation to build.
    const rollDamp = airframe.rollDamp * (1 - FLIGHT.stallDampLoss * depth);

    let pDot = rollCmd * airframe.rollAuthority * rollAuthority - rollDamp * p;
    let qDot = pitchCmd * airframe.pitchAuthority * tailAuthority - airframe.pitchDamp * q;
    let rDot = yawCmd * airframe.yawAuthority * tailAuthority - airframe.yawDamp * r;

    // The standing bias from asymmetric damage. Scaled by dynamic pressure like everything else
    // aerodynamic: a damaged wing pulls harder the faster you go, so slowing down helps — which
    // is a real technique and one the player can discover.
    pDot += this.damage.rollBiasRadS2 * authority;
    rDot += this.damage.yawBiasRadS2 * authority;

    // Weathervaning: the fin pulls the nose toward the relative wind.
    rDot += FLIGHT.weathervaneGain * betaRad * authority;

    // Adverse yaw: rolling right yaws left.
    rDot -= FLIGHT.adverseYawGain * rollCmd * authority;

    // Static stability (see FLIGHT.pitchStiffness — an addition to SPEC §3.2.3). Pitch restores
    // toward the trimmed α; roll restores through dihedral, where a dropped wing slips and the
    // slip rolls it back level. Together these are what let the aircraft fly hands-off in air
    // that is actually moving.
    qDot -= FLIGHT.pitchStiffness * (state.alphaRad - state.trimAlphaRad) * authority;
    pDot -= FLIGHT.dihedralGain * betaRad * authority;

    // Autorotation. Past the stall the down-going wing is deeper into it than the up-going one,
    // so any roll rate amplifies instead of damping. Sideslip is the seed: a clean, balanced
    // stall mushes, while a stall with any slip in it drops a wing and departs.
    if (depth > 0) {
      const asymmetry = p + FLIGHT.autorotationSeedRadS * Math.tanh(betaRad / 0.05);
      pDot += FLIGHT.autorotationGain * depth * asymmetry;
      rDot += FLIGHT.spinYawCoupling * depth * p;
    }

    // Euler cross-coupling from the inertia tensor: a fast roll pitches and yaws.
    const [inertiaRoll, inertiaPitch, inertiaYaw] = airframe.inertia;
    pDot += ((inertiaPitch - inertiaYaw) / inertiaRoll) * q * r;
    qDot += ((inertiaYaw - inertiaRoll) / inertiaPitch) * r * p;
    rDot += ((inertiaRoll - inertiaPitch) / inertiaYaw) * p * q;

    // Turbulence perturbs the rates, not the position, so it reads as air rather than as noise.
    this.atmosphere.sampleTurbulence(altitudeM, this.elapsedS, turbulenceScratch);
    let buffet = 1;
    if (state.airspeedMs > airframe.vneMs) {
      buffet +=
        (FLIGHT.buffetGainRadS2 * (state.airspeedMs - airframe.vneMs)) / FLIGHT.buffetReferenceMs;
    }
    pDot += turbulenceScratch.x * buffet;
    qDot += turbulenceScratch.y * buffet;
    rDot += turbulenceScratch.z * buffet;

    state.angularVelocity.x = p + pDot * dtS;
    state.angularVelocity.y = q + qDot * dtS;
    state.angularVelocity.z = r + rDot * dtS;

    // Integrate the body rates into the orientation and renormalise (SPEC §4.4).
    state.bodyRotationVector(rotationScratch);
    const rotationRate = rotationScratch.length();
    if (rotationRate > 0) {
      axisScratch.copy(rotationScratch).multiplyScalar(1 / rotationRate);
      deltaOrientationScratch.setFromAxisAngle(axisScratch, rotationRate * dtS);
      state.orientation.multiply(deltaOrientationScratch);
      state.orientation.normalize();
    }
  }

  /** Fuel burn, flameout, and the structural limits of SPEC §3.2.4. */
  private updateFuelAndDamage(dtS: number, airspeedMs: number): void {
    // A punctured tank empties whether or not the engine is burning from it.
    if (this.damage.fuelLeakKgS > 0) {
      this.state.fuelKg = Math.max(0, this.state.fuelKg - this.damage.fuelLeakKgS * dtS);
    }
    const state = this.state;
    const airframe = this.airframe;

    if (state.engineRunning && state.throttleActual > 0 && state.fuelKg > 0) {
      state.fuelKg = Math.max(0, state.fuelKg - airframe.fuelBurnKgS * state.throttleActual * dtS);
      state.massKg = airframe.massKg - (airframe.fuelKg - state.fuelKg);
      if (state.fuelKg <= 0) state.engineRunning = false;
    }

    // Over-stress goes through the same per-system model as everything else. Writing it into
    // `state.integrity` directly gave that number a second owner, and `syncDamageToState` then
    // overwrote it on the next detonation — so a missile hit *healed* a worn airframe.
    const n = state.loadFactor;
    const overG =
      n > airframe.gLimitPos ? n - airframe.gLimitPos : n < airframe.gLimitNeg ? airframe.gLimitNeg - n : 0;
    if (overG > 0) {
      applyAttrition(this.damage, FLIGHT.overGIntegrityPerGS * overG * dtS);
    }
    if (airspeedMs > airframe.vneMs) {
      applyAttrition(this.damage, FLIGHT.overSpeedIntegrityPerMsS * (airspeedMs - airframe.vneMs) * dtS);
    }
    if (Math.abs(n) >= airframe.gFailure) {
      destroyAirframe(this.damage);
    }

    this.syncDamageToState();
    const damage = 1 - state.integrity;
    if (damage >= DAMAGE.engineLossThreshold) state.engineRunning = false;
    if (damage >= DAMAGE.structuralThreshold) state.structuralFailure = true;
  }

  /**
   * Surface contact by swept heightfield sampling (SPEC §4.4).
   *
   * Points are tested along the tick's motion segment rather than at its end, because at 90 m/s
   * in a dive the aircraft covers most of a metre per tick and a ridge crest between the two
   * endpoints would otherwise be flown straight through. No mesh collider is involved: a
   * heightfield sample is O(1) and cannot tunnel.
   */
  private resolveGroundContact(): void {
    const state = this.state;
    const samples = FLIGHT.groundSweepSamples;

    for (let i = 1; i <= samples; i += 1) {
      const t = i / samples;
      sweepScratch.lerpVectors(state.previousPosition, state.position, t);
      const terrainY =
        this.ground === null
          ? this.groundHeightM
          : this.ground.heightAt(sweepScratch.x, sweepScratch.z);
      // Over water the seabed is far below the surface; the surface is what you hit.
      const groundY = terrainY > this.seaLevelM ? terrainY : this.seaLevelM;

      if (sweepScratch.y <= groundY) {
        state.position.copy(sweepScratch);
        state.position.y = groundY;
        state.velocity.set(0, 0, 0);
        state.angularVelocity.set(0, 0, 0);
        // Clear the derived readouts too. `tick` early-returns once crashed, so anything left
        // here is frozen at its last airborne value and the instruments go on reporting a flight
        // that ended — a wreck showing 113 m/s beside a ground speed of zero.
        state.airspeedMs = 0;
        state.alphaRad = 0;
        state.betaRad = 0;
        state.dynamicPressurePa = 0;
        state.loadFactor = 0;
        state.liftN = 0;
        state.dragN = 0;
        state.thrustN = 0;
        state.stalled = false;
        state.stallDepth = 0;
        destroyAirframe(this.damage);
        this.syncDamageToState();
        state.structuralFailure = true;
        state.engineRunning = false;
        this.crashed = true;
        return;
      }
    }
  }

  checksum(into: Checksum): void {
    this.state.checksum(into);
    into.addInt(this.crashed ? 1 : 0);
  }
}
